import { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CollapsibleSection from './CollapsibleSection';
import type { GroupRoomBlock, GroupRoomRequest, RoomRequestStatusExtras } from '../../types/groups';
import { plural } from '../../lib/plural';

/** Where the booking came from. Advisory — it never decides what a guest sees. */
const SOURCES = [
  { value: 'tripsby', label: 'Tripsby booked it' },
  { value: 'room_block', label: 'They booked into the block themselves' },
  { value: 'legacy', label: "Not sure / don't record it" },
] as const;

const money = (value: string): number | undefined =>
  value !== '' && Number.isFinite(Number(value)) ? Number(value) : undefined;

/** Whole nights between two yyyy-mm-dd dates, or null if either is missing. */
function nightsBetween(arrival: string, departure: string): number | null {
  if (!arrival || !departure) return null;
  const from = Date.parse(arrival);
  const to = Date.parse(departure);
  if (Number.isNaN(from) || Number.isNaN(to) || to <= from) return null;
  return Math.round((to - from) / 86_400_000);
}

export default function MarkBookedDialog({
  request,
  block,
  saving,
  onClose,
  onConfirm,
}: {
  request: GroupRoomRequest;
  block?: GroupRoomBlock;
  /**
   * The save is in flight. The dialog stays mounted until it lands — a failed
   * PATCH must not discard what the host typed — so the button has to say so
   * and refuse a second press, or an unchanged screen invites a repeat submit.
   */
  saving?: boolean;
  onClose: () => void;
  onConfirm: (extras: RoomRequestStatusExtras) => void;
}) {
  const [confirmation, setConfirmation] = useState(request.bookedConfirmation || '');
  const [arrival, setArrival] = useState((request.arrivalDate || '').slice(0, 10));
  const [departure, setDeparture] = useState((request.departureDate || '').slice(0, 10));
  const [roomType, setRoomType] = useState(request.roomType || block?.roomType || '');
  const [source, setSource] = useState<string>(request.reservationSource || 'tripsby');

  // Seeded from the reservation only — never from the block. The old dialog
  // seeded its single rate field with the block's nightly rate, which is how a
  // headline figure nobody checked ends up stored as what a guest owes. The
  // block rate is offered below as a button the host has to press.
  const [base, setBase] = useState(request.rateBaseAmount?.toString() ?? '');
  const [taxes, setTaxes] = useState(request.rateTaxesAndFeesAmount?.toString() ?? '');
  const [total, setTotal] = useState(request.rateTotalAmount?.toString() ?? '');
  const [dueAtProperty, setDueAtProperty] = useState(
    request.rateDueAtPropertyAmount?.toString() ?? '',
  );
  const [currency, setCurrency] = useState(request.rateCurrency || block?.currency || 'USD');

  // Two separate questions. "Is this number right for this guest?" and "should
  // they see it?" — the first has to be answered before the second can be.
  //
  // Both start from what is stored, not from false. The amounts above are
  // pre-filled from the reservation, so a host who opens this on an already
  // confirmed booking and saves without changing anything would otherwise
  // re-send the same figures unconfirmed — withdrawing a rate the guest can
  // currently see, silently, as a side effect of looking at it.
  const [rateConfirmed, setRateConfirmed] = useState(request.rateConfirmedAt != null);
  const [guestCanViewRate, setGuestCanViewRate] = useState(Boolean(request.guestCanViewRate));

  const [hotelName, setHotelName] = useState(request.hotelName || '');
  const [hotelAddress, setHotelAddress] = useState(request.hotelAddress || '');
  const [hotelPhone, setHotelPhone] = useState(request.hotelPhone || '');

  // Editing any amount withdraws the tick, so the host re-affirms the number
  // they are actually looking at. Without this the approval carried over from a
  // previous figure would ride onto a new one — which is the whole failure this
  // dialog exists to prevent, just slower.
  const editAmount = (set: (value: string) => void) => (value: string) => {
    set(value);
    setRateConfirmed(false);
    setGuestCanViewRate(false);
  };

  const nights = nightsBetween(arrival, departure);
  const blockRate = block?.nightlyRate ?? null;
  const suggestedBase =
    blockRate != null && nights != null ? blockRate * nights * (request.quantity || 1) : null;

  const enteredAmounts = [base, taxes, total, dueAtProperty].some((v) => v !== '');

  // A total that is not base + taxes is often right (a discount, a resort fee
  // outside the split) and often a typo. Say so; never silently rewrite it.
  const sumMismatch = useMemo(() => {
    const b = money(base);
    const t = money(taxes);
    const grand = money(total);
    if (b === undefined || t === undefined || grand === undefined) return null;
    return Math.abs(b + t - grand) > 0.005 ? (b + t).toFixed(2) : null;
  }, [base, taxes, total]);

  const datesInvalid = Boolean(arrival && departure && nights === null);
  const missing = [
    confirmation.trim() ? null : 'confirmation number',
    arrival ? null : 'arrival date',
    departure ? null : 'departure date',
  ].filter(Boolean) as string[];

  const handleConfirm = () => {
    onConfirm({
      bookedConfirmation: confirmation.trim() || undefined,
      arrivalDate: arrival || undefined,
      departureDate: departure || undefined,
      roomType: roomType.trim() || undefined,
      reservationSource: source,
      hotelName: hotelName.trim() || undefined,
      hotelAddress: hotelAddress.trim() || undefined,
      hotelPhone: hotelPhone.trim() || undefined,
      rateBaseAmount: money(base),
      rateTaxesAndFeesAmount: money(taxes),
      rateTotalAmount: money(total),
      rateDueAtPropertyAmount: money(dueAtProperty),
      rateCurrency: currency.trim() ? currency.trim().toUpperCase() : undefined,
      // Always sent, so an edited rate that the host did not re-confirm goes
      // back to unconfirmed rather than inheriting the previous approval.
      rateConfirmed,
      guestCanViewRate: rateConfirmed && guestCanViewRate,
    });
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Assign room / mark booked</DialogTitle>
      <DialogContent>
        <Stack spacing={0.5} sx={{ mb: 2 }}>
          <Typography fontWeight={700}>{request.guestName}</Typography>
          <Typography variant="body2" color="text.secondary">
            {block ? block.hotelName : request.roomBlockId} · party {request.partySize} ·{' '}
            {plural(request.quantity, 'room')}
          </Typography>
        </Stack>

        <Stack spacing={2}>
          <TextField
            fullWidth
            label="Confirmation number"
            value={confirmation}
            onChange={(e) => setConfirmation(e.target.value)}
          />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              fullWidth
              label="Check in"
              type="date"
              value={arrival}
              onChange={(e) => setArrival(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth
              label="Check out"
              type="date"
              value={departure}
              onChange={(e) => setDeparture(e.target.value)}
              InputLabelProps={{ shrink: true }}
              error={datesInvalid}
              helperText={
                datesInvalid
                  ? 'Check out has to be after check in.'
                  : nights != null
                    ? `${nights} night${nights === 1 ? '' : 's'}`
                    : ' '
              }
            />
          </Stack>

          <TextField
            fullWidth
            label="Room type"
            value={roomType}
            onChange={(e) => setRoomType(e.target.value)}
            helperText={
              block?.roomType
                ? `The block is "${block.roomType}" — change this if they ended up in something else.`
                : 'What the guest actually booked.'
            }
          />

          <TextField
            select
            fullWidth
            label="Booked through"
            value={source}
            onChange={(e) => setSource(e.target.value)}
            helperText="Recorded so a rate's origin is known later. It doesn't decide what the guest sees."
          >
            {SOURCES.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>

          <Divider />

          <Typography variant="subtitle2" fontWeight={800}>
            What they're paying
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: -1.5 }}>
            Whole stay, not per night. Leave blank if the guest settles with the hotel directly.
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              fullWidth
              label="Room charge"
              type="number"
              value={base}
              onChange={(e) => editAmount(setBase)(e.target.value)}
            />
            <TextField
              fullWidth
              label="Taxes and fees"
              type="number"
              value={taxes}
              onChange={(e) => editAmount(setTaxes)(e.target.value)}
            />
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              fullWidth
              label="Total"
              type="number"
              value={total}
              onChange={(e) => editAmount(setTotal)(e.target.value)}
              helperText={sumMismatch ? `Room charge plus taxes comes to ${sumMismatch}.` : ' '}
            />
            <TextField
              fullWidth
              label="Due at the hotel"
              type="number"
              value={dueAtProperty}
              onChange={(e) => editAmount(setDueAtProperty)(e.target.value)}
              helperText="The part they pay on arrival."
            />
            <TextField
              label="Currency"
              value={currency}
              onChange={(e) => editAmount(setCurrency)(e.target.value)}
              inputProps={{ maxLength: 3 }}
              sx={{ width: 110 }}
            />
          </Stack>

          {suggestedBase != null && base === '' ? (
            <Button
              size="small"
              onClick={() => editAmount(setBase)(suggestedBase.toFixed(2))}
              sx={{ alignSelf: 'flex-start' }}
            >
              Use the block rate ({blockRate} × {nights} night{nights === 1 ? '' : 's'}
              {request.quantity > 1 ? ` × ${request.quantity} rooms` : ''} ={' '}
              {suggestedBase.toFixed(2)})
            </Button>
          ) : null}

          <FormControlLabel
            control={
              <Checkbox
                checked={rateConfirmed}
                onChange={(e) => {
                  setRateConfirmed(e.target.checked);
                  if (!e.target.checked) setGuestCanViewRate(false);
                }}
              />
            }
            label={`I've checked these amounts are what ${request.guestName} is paying`}
          />
          <FormControlLabel
            disabled={!rateConfirmed || !enteredAmounts}
            control={
              <Checkbox
                checked={guestCanViewRate}
                onChange={(e) => setGuestCanViewRate(e.target.checked)}
              />
            }
            label="Show the rate to this guest on their trip page and confirmation"
          />
          {enteredAmounts && !rateConfirmed ? (
            <Alert severity="info">
              Rates vary from guest to guest even inside a block, so nothing is shown until
              someone confirms these are this guest's own. Saved either way.
            </Alert>
          ) : null}

          <CollapsibleSection
            id="mark-booked-hotel"
            title="Staying somewhere other than the block?"
            summary="Overflow hotel, or a guest moved after booking"
          >
            <Stack spacing={2}>
              <TextField
                fullWidth
                label="Hotel name"
                value={hotelName}
                onChange={(e) => setHotelName(e.target.value)}
                helperText={
                  block ? `Blank keeps "${block.hotelName}".` : 'Blank keeps the block’s hotel.'
                }
              />
              <TextField
                fullWidth
                label="Address"
                value={hotelAddress}
                onChange={(e) => setHotelAddress(e.target.value)}
              />
              <TextField
                fullWidth
                label="Hotel phone"
                value={hotelPhone}
                onChange={(e) => setHotelPhone(e.target.value)}
              />
            </Stack>
          </CollapsibleSection>

          {missing.length > 0 ? (
            <Alert severity="warning">
              Saving is fine, but the confirmation can't be sent without the{' '}
              {missing.join(', ')}.
            </Alert>
          ) : (
            <Alert severity="info">
              Saved as booked — send the guest their confirmation from the Requests list when
              everything's set.
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleConfirm} disabled={datesInvalid || saving}>
          {saving ? 'Saving…' : 'Mark booked'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
